import {
  MIN_INTERNAL_LINKS,
  countInternalLinks,
  findButtonPlaceholders,
  findUnlinkedGuideTitles,
} from '@shared/guideHtmlRules';

import { readHeroImage } from './guideHeroImage';
import { htmlReadingStats } from './sections';

/** Every string value in a template guide, joined: where its inline links live. */
export function jsonProse(contentJson: string): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(contentJson || '{}');
  } catch {
    return '';
  }
  const out: string[] = [];
  const walk = (v: unknown) => {
    if (typeof v === 'string') out.push(v);
    else if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === 'object') Object.values(v as Record<string, unknown>).forEach(walk);
  };
  walk(parsed);
  return out.join('\n');
}

/**
 * The candidate list items of a template guide, as HTML the title check can read.
 *
 * A string that sits in an array is a list item on the page ("Read more guides" is usually an array
 * of strings), so it is wrapped as one; any string may also carry its own `<p>` and `<li>` markup,
 * which is passed through as it is.
 */
export function jsonListBlocks(contentJson: string): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(contentJson || '{}');
  } catch {
    return '';
  }
  const out: string[] = [];
  const walk = (v: unknown, inArray: boolean) => {
    if (typeof v === 'string') {
      if (inArray) out.push(`<li>${v}</li>`);
      if (/<(p|li)\b/i.test(v)) out.push(v);
    } else if (Array.isArray(v)) v.forEach((item) => walk(item, true));
    else if (v && typeof v === 'object') Object.values(v as Record<string, unknown>).forEach((item) => walk(item, false));
  };
  walk(parsed, false);
  return out.join('\n');
}

/** How many labels the placeholder warning names before it stops listing. */
export const MAX_LISTED_PLACEHOLDERS = 5;

/** A published guide, as GET /api/guides/titles lists it. */
export interface GuideTitle {
  slug: string;
  title: string;
  pageType?: string;
}

/** Below this a guide reads as a stub on a public page. */
export const MIN_WORDS = 300;

export interface BodyWarning {
  id: string;
  level: 'warning';
  message: string;
}

/**
 * The soft checks on a guide body: things worth fixing, none of them a reason to refuse a publish.
 *
 * Kept out of the shell so they can be tested without rendering an editor. `jsonWords` is the
 * envelope's count, used only when there is no HTML body to count instead.
 */
export function bodyWarnings({
  contentHtml,
  contentJson,
  jsonWords,
  guideTitles = [],
  slug = '',
}: {
  contentHtml: string;
  contentJson: string;
  jsonWords: number;
  /** Published guides, for the unlinked-title check. Empty skips it. */
  guideTitles?: readonly GuideTitle[];
  /** This guide's slug, so its own title is never reported. */
  slug?: string;
}): BodyWarning[] {
  const items: BodyWarning[] = [];
  const hasHtml = contentHtml.trim().length > 0;
  const words = hasHtml ? htmlReadingStats(contentHtml).words : jsonWords;

  if (words > 0 && words < MIN_WORDS) {
    items.push({
      id: 'short',
      level: 'warning',
      message: `${words} words. Under ${MIN_WORDS} tends to read as a stub on a public page.`,
    });
  }

  /*
   * A template guide links from its prose fields now: the structured generator writes `<a href>`
   * into introduction, content and description, so the same floor applies. Read from the parsed
   * values, because in the JSON text every quote is escaped and `href=\"/x\"` matches nothing.
   */
  const prose = hasHtml ? contentHtml : jsonProse(contentJson);
  if (prose.trim()) {
    const links = countInternalLinks(prose);
    if (links < MIN_INTERNAL_LINKS) {
      items.push({
        id: 'internal-links',
        level: 'warning',
        message: `${links} internal ${links === 1 ? 'link' : 'links'} in the ${hasHtml ? 'body' : 'guide'}; aim for ${MIN_INTERNAL_LINKS} or more to other guides and blogs.`,
      });
    }
  }

  /*
   * Author button placeholders. The site links the known labels at render and prints the rest as
   * plain words, so this says which is which before the guide goes out, never refuses it.
   */
  const placeholders = findButtonPlaceholders(prose);
  if (placeholders.length) {
    const listed = placeholders
      .slice(0, MAX_LISTED_PLACEHOLDERS)
      .map((label) => `[${label}]`)
      .join(', ');
    const more =
      placeholders.length > MAX_LISTED_PLACEHOLDERS
        ? ` and ${placeholders.length - MAX_LISTED_PLACEHOLDERS} more`
        : '';
    items.push({
      id: 'button-placeholders',
      level: 'warning',
      message: `Button placeholders found: ${listed}${more}. Known ones become links on the site automatically; others show as plain words. Replace them with real links if you want a different page.`,
    });
  }

  // A "Read more guides" list typed as plain titles: the reader sees a guide's name and cannot open it.
  const ownSlug = slug.trim();
  const others = guideTitles.filter((g) => g.slug !== ownSlug).map((g) => g.title);
  const unlinked = findUnlinkedGuideTitles(hasHtml ? contentHtml : jsonListBlocks(contentJson), others);
  if (unlinked.length) {
    const listed = unlinked.slice(0, MAX_LISTED_PLACEHOLDERS).join(', ');
    const more =
      unlinked.length > MAX_LISTED_PLACEHOLDERS ? ` and ${unlinked.length - MAX_LISTED_PLACEHOLDERS} more` : '';
    items.push({
      id: 'unlinked-titles',
      level: 'warning',
      message: `Guide titles listed without links: ${listed}${more}. Link them to the guide so readers can open it.`,
    });
  }

  if (!hasHtml) return items;

  /*
   * The banner is `seo.ogImage`, not the first picture in the body. An author who drops an image
   * into the body reasonably expects it to be the banner, and the guide then ships with a topic tile
   * on its card and no banner at all.
   */
  if (/<img\b/i.test(contentHtml) && !readHeroImage(contentJson)) {
    items.push({
      id: 'no-banner',
      level: 'warning',
      message:
        'The body has an image but no banner image is set. Set one under Hero image so it shows on the guide card and at the top of the page.',
    });
  }

  return items;
}
